import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Popover,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { GoogleMap, OverlayView, Polygon, Polyline, useLoadScript } from "@react-google-maps/api";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  normalizeType,
  isNonSensorDevice,
  isOnOffDevice,
  isSensorAlert,
  isSensorNearThreshold,
  isInactiveState,
  formatDeviceType,
} from "../../utils/deviceStatusHelpers";
import batteryIcon from "../../images/device/battery.svg";
import cctvIcon from "../../images/device/cctv.svg";
import fuelIcon from "../../images/device/fuel.svg";
import gpsIcon from "../../images/device/gps.svg";
import humidityIcon from "../../images/device/humidity.svg";
import microphoneIcon from "../../images/device/microphone.svg";
import networkIcon from "../../images/device/network.svg";
import pressureIcon from "../../images/device/pressure.svg";
import rfidIcon from "../../images/device/rfid.svg";
import speakerIcon from "../../images/device/speaker.svg";
import temperatureIcon from "../../images/device/temperature.svg";
import vibrationIcon from "../../images/device/vibration.svg";
import deviceIcon from "../../images/navbar/device_icon.svg";
import config from "../../config.js";
import {
  mapStyles,
  normalizeMapView,
} from "../../components/dashboard/functions";
import { getDeviceMonitorAlerts } from "./deviceApi";

const HISTORY_LENGTH = 7;

const LINE_COLORS = [
  "#AF0000",
  "#185FA5",
  "#1D7A5C",
  "#B45309",
  "#7C3AED",
  "#DB2777",
  "#0EA5E9",
  "#65A30D",
];

const hashString = (value) =>
  String(value || "")
    .split("")
    .reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) >>> 0, 0);

const colorForKey = (key) => LINE_COLORS[hashString(key) % LINE_COLORS.length];

const ALERT_SENSOR_COLORS = [
  "#BFD3EA",
  "#99BEE5",
  "#F53A2E",
  "#6FA4DA",
  "#B7CEE8",
  "#3276C7",
  "#86AEDC",
  "#FF781C",
];
const EMPTY_ORGANIZATION_IDS = [];
const DEFAULT_ANALYTICS_WIDGETS = [
  {
    id: "deviceTypeDistribution",
    widgetKey: "deviceTypeDistribution",
    labelKey: "Device Type Distribution",
  },
  {
    id: "deviceHealth",
    widgetKey: "deviceHealth",
    labelKey: "Device Health",
  },
  {
    id: "highestAlertAssets",
    widgetKey: "highestAlertAssets",
    labelKey: "Vehicles with Highest Alerts",
  },
  {
    id: "alertsBySensors",
    widgetKey: "alertsBySensors",
    labelKey: "Alerts by Sensors",
    props: { gridColumn: { lg: "span 2" } },
  },
  {
    id: "installationLocationAlerts",
    widgetKey: "installationLocationAlerts",
    labelKey: "Alerts by Installation Location (Sites)",
    props: { gridColumn: { lg: "span 2" } },
  },
  {
    id: "hourlyAlertPattern",
    widgetKey: "hourlyAlertPattern",
    labelKey: "24 Hour Alert Pattern",
    props: { gridColumn: { lg: "span 2" } },
  },
];

const getAnalyticsWidgetKey = (widget = {}) =>
  widget.widgetKey || widget.metricKey || widget.cardKey || widget.id || "";

const getAnalyticsWidgetTitle = (widget = {}, fallbackTitle) =>
  widget.labelKey || widget.titleKey || fallbackTitle;

const getAnalyticsWidgetGridSx = (widget = {}, fallbackSx = {}) => {
  const span = widget.span ?? widget.props?.span;
  const gridColumn =
    widget.gridColumn || widget.props?.gridColumn || (span ? { lg: `span ${span}` } : undefined);

  return {
    ...fallbackSx,
    ...(gridColumn ? { gridColumn } : {}),
    ...(widget.sx || {}),
    ...(widget.props?.sx || {}),
  };
};

const ANALYTICS_STATUS_COLORS = {
  ACTIVE: "#1AA67A",
  CRITICAL: "#9A0D0D",
  WARNING: "#FF1010",
  INACTIVE: "#9A0D0D",
};

const getAlertVehicleName = (alert) => {
  const trackingDevice = alert?.trackingDevice || alert?.device || {};
  const vehicle =
    alert?.vehicle ||
    alert?.Vehicle ||
    trackingDevice?.vehicle ||
    trackingDevice?.Vehicle ||
    trackingDevice?.assignedVehicle;
  const vehicleName =
    alert?.vehicleName ||
    vehicle?.name ||
    vehicle?.VehicleNumber ||
    vehicle?.vehicleNumber ||
    vehicle?.registrationNumber ||
    "";

  if (vehicleName) return vehicleName;
  if (alert?.vehicleId) return `Vehicle ${alert.vehicleId}`;
  return "";
};

const getAlertStopName = (alert) => {
  const trackingDevice = alert?.trackingDevice || alert?.device || {};
  const stop =
    alert?.stop ||
    alert?.Stop ||
    trackingDevice?.stop ||
    trackingDevice?.Stop ||
    trackingDevice?.assignedStop;
  const stopName = alert?.stopName || stop?.name || stop?.stopId || "";

  if (stopName) return stopName;
  if (alert?.stopId) return `Stop ${alert.stopId}`;
  return "";
};

const getAlertSensorType = (alert) => {
  const trackingDevice = alert?.trackingDevice || alert?.device || {};
  const rawType =
    alert?.sensorType ||
    trackingDevice?.sensorType ||
    trackingDevice?.deviceType?.name ||
    trackingDevice?.deviceType ||
    alert?.deviceType ||
    alert?.alertType ||
    "Alert";

  return formatDeviceType(rawType);
};

const isCriticalAlertRecord = (alert) => {
  const severity = String(alert?.severity || "").toUpperCase();
  const alertType = String(alert?.alertType || alert?.type || "").toUpperCase();
  return severity === "HIGH" || severity === "CRITICAL" || alertType.includes("THRESHOLD");
};

const getAlertRecordKey = (alert = {}) =>
  String(
    alert.id ||
      alert.deviceAlertId ||
      alert.alertId ||
      [
        alert.trackingDeviceId || alert.trackingDevice?.id,
        alert.alertType || alert.type || alert.sensorType || alert.deviceType,
        alert.vehicleId || alert.stopId || "",
        alert.createdAt || alert.recordedAt || alert.updatedAt || "",
      ]
        .filter(Boolean)
        .join("-"),
  );

const getStartOfDay = (date = new Date()) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const getEndOfDay = (date = new Date()) => {
  const normalized = new Date(date);
  normalized.setHours(23, 59, 59, 999);
  return normalized;
};

const getAnalyticsRangeParams = (range = { label: "Today", multiplier: 1 }) => {
  if (range?.label === "Custom" && range.startDate && range.endDate) {
    return {
      from: getStartOfDay(range.startDate).toISOString(),
      to: getEndOfDay(range.endDate).toISOString(),
    };
  }

  const today = getStartOfDay();
  if (range?.label === "Yesterday") {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    return {
      from: yesterday.toISOString(),
      to: getEndOfDay(yesterday).toISOString(),
    };
  }

  const days = Math.max(1, Number(range?.multiplier) || 1);
  const start = new Date(today);
  start.setDate(today.getDate() - (days - 1));

  return {
    from: start.toISOString(),
    to: getEndOfDay(today).toISOString(),
  };
};

const isAlertWithinRange = (alert, rangeParams = {}) => {
  const recordedAt = alert?.createdAt || alert?.recordedAt || alert?.updatedAt;
  if (!recordedAt) return true;

  const time = new Date(recordedAt).getTime();
  if (Number.isNaN(time)) return true;

  const fromTime = rangeParams.from ? new Date(rangeParams.from).getTime() : null;
  const toTime = rangeParams.to ? new Date(rangeParams.to).getTime() : null;

  if (fromTime !== null && time < fromTime) return false;
  if (toTime !== null && time > toTime) return false;
  return true;
};

const mergeAlertRecords = (...alertGroups) => {
  const merged = new Map();

  alertGroups.flat().forEach((alert) => {
    if (!alert) return;
    const key = getAlertRecordKey(alert);
    merged.set(key, { ...(merged.get(key) || {}), ...alert });
  });

  return Array.from(merged.values()).sort(
    (a, b) =>
      new Date(b.createdAt || b.recordedAt || b.updatedAt || 0).getTime() -
      new Date(a.createdAt || a.recordedAt || a.updatedAt || 0).getTime(),
  );
};

const toCoordinateNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const getEntityCoordinates = (entity = {}) => {
  if (!entity) return null;

  let lat =
    toCoordinateNumber(entity.lat) ??
    toCoordinateNumber(entity.latitude) ??
    toCoordinateNumber(entity.Latitude);
  let lng =
    toCoordinateNumber(entity.lng) ??
    toCoordinateNumber(entity.longitude) ??
    toCoordinateNumber(entity.Longitude);

  if (lat === null || lng === null) return null;
  if (lat === 0 && lng === 0) return null;

  const isValidLatLng = (latitude, longitude) =>
    Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180;

  if (!isValidLatLng(lat, lng) && isValidLatLng(lng, lat)) {
    [lat, lng] = [lng, lat];
  }

  if (!isValidLatLng(lat, lng)) return null;
  return { lat, lng };
};

const getSiteCoordinates = (site = {}) => {
  if (!site) return null;

  const directCoordinates = getEntityCoordinates(site);
  if (directCoordinates) return directCoordinates;
  const siteCoordinates = Array.isArray(site?.sitCoordinates) ? site.sitCoordinates : [];
  const siteCoordinate = siteCoordinates.find((coordinate) => getEntityCoordinates(coordinate));
  return getEntityCoordinates(siteCoordinate);
};

const getSitePath = (site = {}) => {
  if (!site) return [];

  const siteCoordinates = Array.isArray(site?.sitCoordinates) ? site.sitCoordinates : [];
  return siteCoordinates.map(getEntityCoordinates).filter(Boolean);
};

const getLocationPath = (...entities) => {
  for (const entity of entities) {
    const path = getSitePath(entity?.site || entity);
    if (path.length) return path;
  }

  return [];
};

const getAlertEntityKey = (alert) => {
  if (alert?.vehicleId) return `vehicle-${alert.vehicleId}`;
  if (alert?.stopId) return `stop-${alert.stopId}`;
  const vehicleName = getAlertVehicleName(alert);
  if (vehicleName) return `vehicle-${vehicleName}`;
  const stopName = getAlertStopName(alert);
  if (stopName) return `stop-${stopName}`;
  return `alert-${alert?.id || alert?.trackingDeviceId || alert?.createdAt || "unknown"}`;
};

const getAlertEntityName = (alert, t) => {
  const vehicleName = getAlertVehicleName(alert);
  if (vehicleName) return vehicleName;
  const stopName = getAlertStopName(alert);
  if (stopName) return stopName;
  return t("Unknown Location");
};

const buildAlertLocationMarkers = ({ alerts, devices, stops, t }) => {
  const stopsById = new Map();
  const stopsByName = new Map();
  const stopsBySiteId = new Map();
  stops.forEach((stop) => {
    [stop?.id, stop?.stopId].filter(Boolean).forEach((key) => {
      stopsById.set(String(key), stop);
    });
    [stop?.name, stop?.stopId].filter(Boolean).forEach((key) => {
      stopsByName.set(String(key).toLowerCase(), stop);
    });
    [stop?.siteId, stop?.site?.id, stop?.site?.siteId].filter(Boolean).forEach((key) => {
      if (!stopsBySiteId.has(String(key))) stopsBySiteId.set(String(key), stop);
    });
  });

  const devicesByKey = new Map();
  const vehiclesById = new Map();
  devices.forEach((device) => {
    [
      device?.id,
      device?.trackingDeviceId,
      device?.deviceId,
      device?.name,
    ]
      .filter(Boolean)
      .forEach((key) => devicesByKey.set(String(key), device));

    const vehicle = device?.assignedVehicle || device?.Vehicle || device?.vehicle;
    const vehicleLike =
      vehicle ||
      (device?.associationType === "Vehicle"
        ? {
            id: device?.associationId || device?.vehicleId,
            name: device?.associationName || device?.vehicleName,
            site: device?.site,
            siteId: device?.siteId,
            lat: device?.lat,
            lng: device?.lng,
          }
        : null);

    [
      vehicleLike?.id,
      vehicleLike?.vehicleId,
      device?.vehicleId,
      device?.associationType === "Vehicle" ? device?.associationId : "",
    ]
      .filter(Boolean)
      .forEach((key) => vehiclesById.set(String(key), vehicleLike));
  });

  const markersByKey = new Map();
  const upsertMarker = ({
    key,
    name,
    coordinates,
    path = [],
    criticalCount = 0,
    warningCount = 0,
    activeCount = 0,
  }) => {
    if (!key || !coordinates) return;

    const existing = markersByKey.get(key);
    markersByKey.set(key, {
      id: key,
      name: existing?.name || name || t("Unknown Location"),
      lat: existing?.lat ?? coordinates.lat,
      lng: existing?.lng ?? coordinates.lng,
      path: existing?.path?.length ? existing.path : path,
      count: (existing?.count || 0) + criticalCount + warningCount + activeCount,
      criticalCount: (existing?.criticalCount || 0) + criticalCount,
      warningCount: (existing?.warningCount || 0) + warningCount,
      activeCount: (existing?.activeCount || 0) + activeCount,
    });
  };

  devices.forEach((device, index) => {
    const vehicle = device?.assignedVehicle || device?.Vehicle || device?.vehicle;
    const stop = device?.assignedStop || device?.Stop || device?.stop;
    const associationType = String(device?.associationType || "").toLowerCase();
    const key =
      associationType === "vehicle" && device?.associationId
        ? `vehicle-${device.associationId}`
        : associationType === "stop" && device?.associationId
          ? `stop-${device.associationId}`
          : vehicle?.id || device?.vehicleId
            ? `vehicle-${vehicle?.id || device?.vehicleId}`
            : stop?.id || stop?.stopId || device?.stopId
              ? `stop-${stop?.id || stop?.stopId || device?.stopId}`
              : `device-${device?.id || device?.trackingDeviceId || device?.deviceId || index}`;
    const coordinates =
      getEntityCoordinates(vehicle) ||
      getEntityCoordinates(stop) ||
      getEntityCoordinates(device) ||
      getSiteCoordinates(device?.site) ||
      getSiteCoordinates(vehicle?.site) ||
      getSiteCoordinates(stop?.site);

    upsertMarker({
      key,
      name: device?.associationName || vehicle?.name || stop?.name || device?.name,
      coordinates,
      path: getLocationPath(device, vehicle, stop),
      activeCount: 1,
    });
  });

  alerts.forEach((alert) => {
    const trackingDevice = alert?.trackingDevice || alert?.device || {};
    const relatedDevice =
      devicesByKey.get(String(alert?.trackingDeviceId || "")) ||
      devicesByKey.get(String(trackingDevice?.id || "")) ||
      devicesByKey.get(String(trackingDevice?.deviceId || ""));
    const alertStop = alert?.stop || alert?.Stop;
    const inventoryStop =
      trackingDevice?.assignedStop ||
      relatedDevice?.assignedStop ||
      relatedDevice?.Stop ||
      relatedDevice?.stop ||
      stopsById.get(String(alert?.stopId || alertStop?.id || alertStop?.stopId || "")) ||
      stopsByName.get(String(getAlertStopName(alert)).toLowerCase());
    const stop =
      inventoryStop || alertStop
        ? { ...(inventoryStop || {}), ...(alertStop || {}) }
        : null;
    const alertVehicle = alert?.vehicle || alert?.Vehicle;
    const inventoryVehicle =
      trackingDevice?.assignedVehicle ||
      relatedDevice?.assignedVehicle ||
      relatedDevice?.Vehicle ||
      relatedDevice?.vehicle ||
      vehiclesById.get(String(alert?.vehicleId || alertVehicle?.id || "")) ||
      (relatedDevice?.associationType === "Vehicle"
        ? {
            id: relatedDevice?.associationId,
            name: relatedDevice?.associationName,
            site: relatedDevice?.site,
            siteId: relatedDevice?.siteId,
            lat: relatedDevice?.lat,
            lng: relatedDevice?.lng,
          }
        : null);
    const vehicle =
      inventoryVehicle || alertVehicle
        ? { ...(inventoryVehicle || {}), ...(alertVehicle || {}) }
        : null;
    const siteStop =
      stopsBySiteId.get(String(vehicle?.siteId || "")) ||
      stopsBySiteId.get(String(vehicle?.site?.id || "")) ||
      stopsBySiteId.get(String(vehicle?.site?.siteId || "")) ||
      stopsBySiteId.get(String(relatedDevice?.siteId || "")) ||
      stopsBySiteId.get(String(relatedDevice?.site?.id || "")) ||
      stopsBySiteId.get(String(relatedDevice?.site?.siteId || ""));
    const coordinates =
      getEntityCoordinates(alert) ||
      getEntityCoordinates(vehicle) ||
      getEntityCoordinates(stop) ||
      getEntityCoordinates(relatedDevice) ||
      getSiteCoordinates(relatedDevice?.site) ||
      getSiteCoordinates(vehicle?.site) ||
      getSiteCoordinates(stop?.site) ||
      getEntityCoordinates(siteStop) ||
      getSiteCoordinates(siteStop?.site);

    const key = getAlertEntityKey(alert);
    const isCritical = isCriticalAlertRecord(alert);
    upsertMarker({
      key,
      name: getAlertEntityName(alert, t),
      coordinates,
      path: getLocationPath(relatedDevice, vehicle, stop, siteStop),
      criticalCount: isCritical ? 1 : 0,
      warningCount: isCritical ? 0 : 1,
    });
  });

  return Array.from(markersByKey.values())
    .sort(
      (a, b) =>
        b.criticalCount - a.criticalCount ||
        b.warningCount - a.warningCount ||
        b.count - a.count,
    )
    .slice(0, 10);
};

// Unique per sensor even if associationName/id collide across devices —
// associationType + associationId + id + array index as a final tiebreaker.
const getSensorKey = (device, index) =>
  [
    device.associationType,
    device.associationId ?? device.associationName,
    device.id,
    index,
  ]
    .filter((part) => part !== undefined && part !== null && part !== "")
    .join("-");

const RoundedBar = (props) => {
  const { fill, x, y, width, height } = props;
  const safeHeight = Number.isFinite(height) ? Math.max(height, 0) : 0;
  const radius = Math.min(8, safeHeight / 2);

  if (safeHeight <= 0 || !Number.isFinite(x) || !Number.isFinite(y))
    return null;
  if (safeHeight <= radius || width <= 0) {
    return (
      <rect
        x={x}
        y={y}
        width={Math.max(width, 0)}
        height={safeHeight}
        fill={fill}
      />
    );
  }
  return (
    <g>
      <rect
        x={x}
        y={y + radius}
        width={width}
        height={Math.max(safeHeight - radius, 0)}
        fill={fill}
      />
      <path
        d={`M${x},${y + radius}
            Q${x},${y} ${x + radius},${y}
            L${x + width - radius},${y}
            Q${x + width},${y} ${x + width},${y + radius}
            L${x + width},${y + safeHeight}
            L${x},${y + safeHeight}
            Z`}
        fill={fill}
      />
    </g>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  const { t } = useTranslation();
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          border: "1px solid #ccc",
          borderRadius: "8px",
          padding: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        {label !== undefined && (
          <p style={{ margin: 0, fontWeight: "bold", color: "#333" }}>
            {label}
          </p>
        )}
        {payload.map((entry, index) => (
          <p
            key={index}
            style={{
              margin: "4px 0",
              color: entry.color || entry.payload?.color,
            }}
          >
            {`${t(entry.name, entry.name)}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

function SummaryStat({ label, value, color }) {
  return (
    <Box sx={{ minWidth: 130 }}>
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 700,
          color: color || "#1F242E",
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: "#647490", mt: 0.25 }}>
        {label}
      </Typography>
    </Box>
  );
}

function ChartCard({ title, children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #F1F5F9",
        boxShadow: "0px 1px 2px 0px #0000000D",
        borderRadius: "8px",
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        ...sx,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          textAlign: "center",
          mb: 0.5,
          color: "#183059",
          fontWeight: 600,
          fontSize: "14px",
        }}
      >
        {title}
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
    </Paper>
  );
}

function AnalyticsTile({ title, children, sx, action }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #E7ECF3",
        boxShadow: "0px 1px 2px 0px rgba(15, 23, 42, 0.04)",
        borderRadius: "8px",
        backgroundColor: "#FFFFFF",
        p: 1.5,
        minWidth: 0,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          mb: 1.25,
        }}
      >
        <Typography 
         sx={{
    color: "#1E293B",
    fontFamily: "Roboto",
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "20px",
    letterSpacing: "0px",
    verticalAlign: "middle",
  }}
 >
          {title}
        </Typography>
        {action !== undefined ? (
          action
        ) : (
          <Typography sx={{ color: "#B4BFCE", fontSize: 16 }}>i</Typography>
        )}
      </Box>
      {children}
    </Paper>
  );
}

const getDeviceIconAsset = (type) => {
  const normalized = String(type || "").toLowerCase();
  if (normalized.includes("gps")) return gpsIcon;
  if (normalized.includes("rfid")) return rfidIcon;
  if (normalized.includes("camera") || normalized.includes("cctv") || normalized.includes("television")) return cctvIcon;
  if (normalized.includes("fuel")) return fuelIcon;
  if (normalized.includes("battery")) return batteryIcon;
  if (normalized.includes("speaker")) return speakerIcon;
  if (normalized.includes("microphone") || normalized.includes("mic")) return microphoneIcon;
  if (normalized.includes("vibration")) return vibrationIcon;
  if (normalized.includes("pressure")) return pressureIcon;
  if (normalized.includes("humidity")) return humidityIcon;
  if (normalized.includes("temperature")) return temperatureIcon;
  if (normalized.includes("device")) return deviceIcon;
  return networkIcon;
};

const TinyDeviceIcon = ({ type, color = "#45474C" }) => {
    const icon = getDeviceIconAsset(type);
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        width: 16,
        height: 16,
        display: "inline-block",
        backgroundColor: color,
        mask: `url(${icon}) center / contain no-repeat`,
        WebkitMask: `url(${icon}) center / contain no-repeat`,
      }}
    />
  );
};

const TodayIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M5.33333 4.66667V2M10.6667 4.66667V2M4.66667 7.33333H11.3333M3.33333 14H12.6667C13.4026 14 14 13.4026 14 12.6667V4.66667C14 3.93078 13.4026 3.33333 12.6667 3.33333H3.33333C2.59745 3.33333 2 3.93078 2 4.66667V12.6667C2 13.4026 2.59745 14 3.33333 14L5.33333 4.66667"
      stroke="#4B5563"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CustomizeIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M8.0013 4.00033V2.66699M8.0013 4.00033C7.26542 4.00033 6.66797 4.59777 6.66797 5.33366C6.66797 6.06955 7.26542 6.66699 8.0013 6.66699M8.0013 4.00033C8.73719 4.00033 9.33464 4.59777 9.33464 5.33366C9.33464 6.06955 8.73719 6.66699 8.0013 6.66699M4.0013 12.0003C4.73719 12.0003 5.33464 11.4029 5.33464 10.667C5.33464 9.93111 4.73719 9.33366 4.0013 9.33366M4.0013 12.0003C3.26542 12.0003 2.66797 11.4029 2.66797 10.667C2.66797 9.93111 3.26542 9.33366 4.0013 9.33366M4.0013 12.0003V13.3337M4.0013 9.33366V2.66699M8.0013 6.66699V13.3337M12.0013 12.0003C12.7372 12.0003 13.3346 11.4029 13.3346 10.667C13.3346 9.93111 12.7372 9.33366 12.0013 9.33366M12.0013 12.0003C11.2654 12.0003 10.668 11.4029 10.668 10.667C10.668 9.93111 11.2654 9.33366 12.0013 9.33366M12.0013 12.0003V13.3337M12.0013 9.33366V2.66699"
      stroke="white"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function AssetDistributionActions({ t, onRangeChange }) {
  const [rangeLabel, setRangeLabel] = useState("Today");
  const [customAnchor, setCustomAnchor] = useState(null);
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const rangeOptions = ["Today", "Yesterday", "Last 7 days", "Last 30 days"];

  const handleRangeChange = (event) => {
    const option = event.target.value;
    if (option === "Custom") return;
    setRangeLabel(option);
    onRangeChange?.({
      label: option,
      multiplier:
        option === "Last 30 days"
          ? 30
          : option === "Last 7 days"
              ? 7
              : 1,
    });
  };

  const applyCustomRange = () => {
    const start = customRange.start ? new Date(customRange.start) : null;
    const end = customRange.end ? new Date(customRange.end) : null;
    const days =
      start && end && end >= start
        ? Math.max(1, Math.round((end - start) / 86400000) + 1)
        : 1;
    setRangeLabel("Custom");
    onRangeChange?.({
      label: "Custom",
      multiplier: days,
      startDate: customRange.start,
      endDate: customRange.end,
    });
    setCustomAnchor(null);
  };

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexShrink: 0 }}>
      <Box
        sx={{
          height: 34,
          width: 140,
          minWidth: 140,
          borderRadius: "4px",
          border: "1px solid #E1E6EF",
          backgroundColor: "#FFFFFF",
          boxShadow: "0px 1px 2px 0px #0000000D",
          color: "#4B5563",
          fontFamily: "Inter, Roboto, Arial, sans-serif",
          fontSize: 12,
          fontWeight: 500,
          lineHeight: "16px",
          "& .MuiInputBase-root": {
            width: "100%",
            height: "100%",
            color: "inherit",
            font: "inherit",
          },
          "& .MuiSelect-select": {
            height: "100%",
            minHeight: 0,
            padding: "8px 30px 8px 12px",
            display: "flex",
            alignItems: "center",
            boxSizing: "border-box",
            font: "inherit",
          },
          "& .MuiSelect-icon": {
            right: 8,
            color: "#4B5563",
            fontSize: 18,
          },
        }}
      >
        <Select
          value={rangeLabel}
          onChange={handleRangeChange}
          displayEmpty
          variant="standard"
          disableUnderline
          IconComponent={KeyboardArrowDownIcon}
          MenuProps={{
            PaperProps: {
              sx: {
                minWidth: 148,
                p: "6px",
                border: "1px solid #E1E6EF",
                borderRadius: "4px",
                background: "#FFFFFF",
                boxShadow: "0 8px 20px rgba(15, 23, 42, 0.12)",
                boxSizing: "border-box",
              },
            },
            MenuListProps: { sx: { p: 0 } },
          }}
          renderValue={() => (
            <Box
              component="span"
              sx={{
                width: "100%",
                display: "inline-flex",
                alignItems: "center",
                gap: "16px",
                minWidth: 0,
              }}
            >
              <TodayIcon />
              <Box
                component="span"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t(rangeLabel)}
              </Box>
            </Box>
          )}
        >
          {rangeLabel === "Custom" && (
            <MenuItem value="Custom" sx={{ display: "none" }}>
              {t("Custom")}
            </MenuItem>
          )}
          {rangeOptions.map((option) => (
            <MenuItem
              key={option}
              value={option}
              sx={{
                height: 30,
                minHeight: 30,
                px: 1,
                borderRadius: "3px",
                color: "#334155",
                fontFamily: "Inter, Roboto, Arial, sans-serif",
                fontSize: 12,
                fontWeight: 600,
                "&:hover, &.Mui-selected, &.Mui-selected:hover": {
                  backgroundColor: "#F1F5F9",
                },
              }}
            >
              {t(option)}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Button
        onClick={(event) => setCustomAnchor(event.currentTarget)}
        sx={{
          height: 32,
          minWidth: 48,
          borderRadius: "4px",
          backgroundColor: "#2563EB",
          color: "#FFFFFF",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          px: 2,
          py: 1,
          boxShadow: "0px 1px 2px 0px #0000000D",
          textTransform: "none",
          "&:hover": { backgroundColor: "#1D4ED8" },
        }}
      >
        <CustomizeIcon />
      </Button>
      <Popover
        open={Boolean(customAnchor)}
        anchorEl={customAnchor}
        onClose={() => setCustomAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1.5,
            width: 260,
            border: "1px solid #F1F5F9",
            boxShadow: "0px 6px 18px 0px #0000001A",
          },
        }}
      >
        <Box sx={{ display: "grid", gap: 1.25 }}>
          <TextField
            label={t("Start Date")}
            type="date"
            size="small"
            value={customRange.start}
            onChange={(event) =>
              setCustomRange((prev) => ({ ...prev, start: event.target.value }))
            }
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={t("End Date")}
            type="date"
            size="small"
            value={customRange.end}
            onChange={(event) =>
              setCustomRange((prev) => ({ ...prev, end: event.target.value }))
            }
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            onClick={applyCustomRange}
            sx={{
              height: 34,
              backgroundColor: "#2563EB",
              textTransform: "none",
              fontSize: 12,
              fontWeight: 700,
              "&:hover": { backgroundColor: "#1D4ED8" },
            }}
          >
            {t("Apply")}
          </Button>
        </Box>
      </Popover>
    </Box>
  );
}

const DEFAULT_MAP_CENTER = { lat: 12.95, lng: 77.68 };

function InstallationAlertsMap({ stops = [] }) {
  const { t } = useTranslation();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries: ["places", "directions"],
  });

  const center = useMemo(() => {
    const validStops = stops.filter(
      (stop) => Number.isFinite(stop.lat) && Number.isFinite(stop.lng),
    );
    if (!validStops.length) return DEFAULT_MAP_CENTER;
    return {
      lat:
        validStops.reduce((sum, stop) => sum + stop.lat, 0) / validStops.length,
      lng:
        validStops.reduce((sum, stop) => sum + stop.lng, 0) / validStops.length,
    };
  }, [stops]);

  if (!isLoaded) {
    return (
      <Box
        sx={{
          height: 230,
          border: "1px solid #D8DEE8",
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#647490",
          fontSize: 12,
          backgroundColor: "#F8FAFC",
        }}
      >
        {t("Loading map...")}
      </Box>
    );
  }

  const legendItems = [
    ["Critical", ANALYTICS_STATUS_COLORS.CRITICAL],
    ["Warning", ANALYTICS_STATUS_COLORS.WARNING],
    ["Active", ANALYTICS_STATUS_COLORS.ACTIVE],
  ];

  return (
    <Box
      sx={{
        height: 230,
        border: "1px solid #D8DEE8",
        borderRadius: "4px",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={center}
        zoom={12}
        options={{
          styles: mapStyles,
          mapTypeId: normalizeMapView("roadmap"),
          disableDefaultUI: true,
          zoomControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        }}
      >
        {stops.map((stop) => {
          const path = Array.isArray(stop.path) ? stop.path : [];
          if (path.length < 2) return null;

          const isPolygon = path.length >= 3;
          const pathOptions = {
            strokeColor: "#2563EB",
            strokeOpacity: 0.9,
            strokeWeight: 2,
            fillColor: "#2563EB",
            fillOpacity: 0.06,
          };

          return isPolygon ? (
            <Polygon key={`${stop.id}-path`} paths={path} options={pathOptions} />
          ) : (
            <Polyline key={`${stop.id}-path`} path={path} options={pathOptions} />
          );
        })}
        {stops.map((stop) => {
          const alertCount =
            (Number(stop.criticalCount) || 0) + (Number(stop.warningCount) || 0);
          const count = Math.max(alertCount || Number(stop.count) || 0, 1);
          const isCritical = (Number(stop.criticalCount) || 0) > 0;
          const hasWarning = (Number(stop.warningCount) || 0) > 0;
          const fill = isCritical
            ? ANALYTICS_STATUS_COLORS.CRITICAL
            : hasWarning
              ? ANALYTICS_STATUS_COLORS.WARNING
              : ANALYTICS_STATUS_COLORS.ACTIVE;

          return (
            <OverlayView
              key={stop.id || stop.stopId || stop.name}
              position={{ lat: stop.lat, lng: stop.lng }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <Box
                title={`${stop.name}: ${count}`}
                sx={{
                  transform: "translate(-50%, -50%)",
                  display: "grid",
                  justifyItems: "center",
                  gap: 0.4,
                  pointerEvents: "auto",
                }}
              >
                <Box
                  sx={{
                    width: isCritical ? 42 : 32,
                    height: isCritical ? 42 : 32,
                    borderRadius: "50%",
                    backgroundColor: fill,
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: isCritical ? 18 : 13,
                    fontWeight: 800,
                    border: "2px solid #FFFFFF",
                    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.25)",
                  }}
                >
                  {count}
                </Box>
                <Typography
                  sx={{
                    maxWidth: 92,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: "3px",
                    backgroundColor: "rgba(255,255,255,0.92)",
                    color: "#1F242E",
                    fontSize: 8,
                    fontWeight: 700,
                    lineHeight: "10px",
                    textAlign: "center",
                    boxShadow: "0 1px 3px rgba(15,23,42,0.16)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {stop.name}
                </Typography>
              </Box>
            </OverlayView>
          );
        })}
      </GoogleMap>
      <Box
        sx={{
          position: "absolute",
          top: 14,
          left: 14,
          zIndex: 2,
          backgroundColor: "rgba(255,255,255,0.94)",
          borderRadius: "6px",
          boxShadow: "0 2px 8px rgba(15,23,42,0.12)",
          px: 1.1,
          py: 0.9,
          display: "grid",
          gap: 0.65,
        }}
      >
        {legendItems.map(([label, color]) => (
          <Box
            key={label}
            sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
          >
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: "50%",
                backgroundColor: color,
              }}
            />
            <Typography
              sx={{ color: "#1F242E", fontSize: 11, lineHeight: "13px" }}
            >
              {t(label)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// One line per real sensor, plus a small legend underneath naming each
// sensor and its own min/max — replaces averaging across sensors that have
// different thresholds/units (that hid real signal and was misleading).
function SensorLinesCard({
  title,
  devices,
  deviceType,
  sx,
  sampleIntervalMs = 10000,
}) {
  const { t } = useTranslation();
  // Assign colors by position in this chart's own sensor list — guarantees
  // every sensor shown together gets a distinct color (as long as there are
  // <= LINE_COLORS.length of them), instead of hashing which can collide.
  const sensors = useMemo(
    () =>
      devices
        .filter((d) => normalizeType(d) === deviceType)
        .map((d, index) => {
          const key = getSensorKey(d, index);
          return {
            key,
            label: `${d.associationName || d.name} · ${d.id}`,
            value: Number(d.value),
            min: Number(d.min),
            max: Number(d.max),
            unit: d.unit || "",
            color: LINE_COLORS[index % LINE_COLORS.length],
            inAlert: isSensorAlert(d),
            nearThreshold: isSensorNearThreshold(d),
          };
        }),
    [devices, deviceType],
  );

  const [rows, setRows] = useState(() => {
    const seedRow = {};
    sensors.forEach((s) => (seedRow[s.key] = s.value));
    return Array.from({ length: HISTORY_LENGTH }, () => ({ ...seedRow }));
  });

  // Track real elapsed time so the line history only samples every
  // `sampleIntervalMs`, even though `devices` (and the live legend below)
  // updates every 2s from the parent's poll — prevents the chart from
  // visibly re-plotting every couple of seconds.
  const lastSampleRef = React.useRef(Date.now());

  useEffect(() => {
    const now = Date.now();
    if (now - lastSampleRef.current < sampleIntervalMs) return;
    lastSampleRef.current = now;

    const newRow = {};
    sensors.forEach((s) => (newRow[s.key] = s.value));
    setRows((prev) => [...prev.slice(1), newRow]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices]);

  // Only recompute when `rows` actually changes, not on every parent
  // re-render (devices ticks every 2s but rows only changes every
  // sampleIntervalMs) — avoids a new array reference triggering
  // recharts' enter animation on every poll.
  const labeledRows = useMemo(
    () =>
      rows.map((row, index, arr) => ({
        ...row,
        label:
          index === arr.length - 1
            ? t("Now")
            : `-${Math.round(((arr.length - 1 - index) * sampleIntervalMs) / 1000)}s`,
      })),
    [rows, sampleIntervalMs, t],
  );

  return (
    <ChartCard title={title} sx={sx}>
      {sensors.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 190,
            color: "#647490",
            fontSize: 12,
          }}
        >
          {t("No sensors of this type installed")}
        </Box>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={190}>
            <LineChart
              data={labeledRows}
              margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e0e0e0"
                vertical={false}
              />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#666" }} />
              <YAxis tick={{ fontSize: 10, fill: "#666" }} width={30} />
              <Tooltip content={<CustomTooltip />} />
              {sensors.map((s) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
          <Box
            sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1, px: 0.5 }}
          >
            {sensors.map((s) => (
              <Box
                key={s.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: 10.5,
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    flexShrink: 0,
                    backgroundColor: s.color, // always the line's own color
                    // border: s.inAlert
                    //   ? "2px solid #AF0000"
                    //   : s.nearThreshold
                    //     ? "2px solid #FF0000"
                    //     : "2px solid transparent",
                    boxSizing: "content-box",
                  }}
                />
                <Typography
                  component="span"
                  sx={{ fontSize: 10.5, color: "#647490" }}
                >
                  {s.label}:{" "}
                  <strong style={{ color: "#1F242E" }}>
                    {s.value}
                    {s.unit}
                  </strong>{" "}
                  ({t("range")} {s.min}
                  {s.unit}–{s.max}
                  {s.unit})
                  {/* {s.inAlert && (
                    <strong style={{ color: "#AF0000" }}> · Alert</strong>
                  )}
                  {!s.inAlert && s.nearThreshold && (
                    <strong style={{ color: "#FF0000" }}> · Warning</strong>
                  )} */}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </ChartCard>
  );
}

export default function DeviceAnalyticsSection({
  devices = [],
  stops = [],
  alerts = [],
  widgets = [],
  organizationId,
  organizationIds = EMPTY_ORGANIZATION_IDS,
}) {
  const { t } = useTranslation();
  const [analyticsRange, setAnalyticsRange] = useState({
    label: "Today",
    multiplier: 1,
  });
  const [rangeAlerts, setRangeAlerts] = useState([]);
  const analyticsRangeParams = useMemo(
    () => getAnalyticsRangeParams(analyticsRange),
    [analyticsRange],
  );
  const analyticsAlerts = useMemo(
    () =>
      mergeAlertRecords(
        rangeAlerts,
        alerts.filter((alert) => isAlertWithinRange(alert, analyticsRangeParams)),
      ),
    [alerts, analyticsRangeParams, rangeAlerts],
  );

  useEffect(() => {
    const scopedOrganizationIds = organizationId
      ? [String(organizationId)]
      : Array.from(new Set(organizationIds.filter(Boolean).map(String)));

    if (!scopedOrganizationIds.length) {
      setRangeAlerts([]);
      return;
    }

    let isCancelled = false;

    const fetchRangeAlerts = async () => {
      try {
        const results = await Promise.allSettled(
          scopedOrganizationIds.map((scopedOrganizationId) =>
            getDeviceMonitorAlerts({
              organizationId: scopedOrganizationId,
              status: "all",
              ...analyticsRangeParams,
              limit: 500,
            }),
          ),
        );
        const alertGroups = results
          .filter((result) => result.status === "fulfilled")
          .map((result) => {
            const response = result.value;
            const alertData = Array.isArray(response?.data)
              ? response.data
              : response?.data?.alerts || response?.alerts || response || [];
            return Array.isArray(alertData) ? alertData : [];
          });

        if (!isCancelled) setRangeAlerts(mergeAlertRecords(...alertGroups));
      } catch (error) {
        if (!isCancelled) {
          console.warn("Unable to fetch device analytics alerts:", error);
          setRangeAlerts([]);
        }
      }
    };

    fetchRangeAlerts();

    return () => {
      isCancelled = true;
    };
  }, [analyticsRangeParams, organizationId, organizationIds]);

  const {
    deviceTypeCounts,
    statusBreakdown,
    summary,
    highestAlertAssets,
    alertsBySensor,
    hourlyAlertPattern,
  } = useMemo(() => {
    const typeCounts = new Map();
    const statusCounts = { Normal: 0, Warning: 0, Alert: 0, Inactive: 0 };
    const vehicleAlertCounts = new Map();
    const alertSensorCounts = new Map();
    let criticalAlertCount = 0;
    let warningAlertCount = 0;

    devices.forEach((device) => {
      const typeLabel = t(
        formatDeviceType(device.type),
        formatDeviceType(device.type),
      );
      typeCounts.set(typeLabel, (typeCounts.get(typeLabel) || 0) + 1);

      if (isNonSensorDevice(device) || isOnOffDevice(device)) {
        if (isInactiveState(device)) statusCounts.Inactive += 1;
        else statusCounts.Normal += 1;
      } else if (isSensorAlert(device)) {
        statusCounts.Alert += 1;
      } else if (isSensorNearThreshold(device)) {
        statusCounts.Warning += 1;
      } else {
        statusCounts.Normal += 1;
      }
    });

    analyticsAlerts.forEach((alert) => {
      if (isCriticalAlertRecord(alert)) {
        criticalAlertCount += 1;
      } else {
        warningAlertCount += 1;
      }

      const vehicleName = getAlertVehicleName(alert);
      if (vehicleName) {
        vehicleAlertCounts.set(
          vehicleName,
          (vehicleAlertCounts.get(vehicleName) || 0) + 1,
        );
      }

      const sensorType = t(getAlertSensorType(alert), getAlertSensorType(alert));
      alertSensorCounts.set(
        sensorType,
        (alertSensorCounts.get(sensorType) || 0) + 1,
      );
    });

    const totalDevices = devices.length || 1;
    const highestAlertAssets = Array.from(
      vehicleAlertCounts,
      ([asset, count]) => ({
        asset,
        count,
      }),
    )
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    const alertsBySensor = Array.from(
      alertSensorCounts,
      ([type, count], index) => ({
        type,
        count,
        color: ALERT_SENSOR_COLORS[index % ALERT_SENSOR_COLORS.length],
      }),
    ).slice(0, 8);

  const HOUR_BUCKET_STARTS = [0, 3, 6, 9, 12, 15, 18, 21];
    const criticalByBucket = new Array(HOUR_BUCKET_STARTS.length).fill(0);
   const warningByBucket = new Array(HOUR_BUCKET_STARTS.length).fill(0);

   analyticsAlerts.forEach((alert) => {
      const recordedAt = alert?.createdAt || alert?.updatedAt;
      if (!recordedAt) return;
      const recordedDate = new Date(recordedAt);
      if (Number.isNaN(recordedDate.getTime())) return;
      const bucketIndex = Math.min(
        Math.floor(recordedDate.getHours() / 3),
        HOUR_BUCKET_STARTS.length - 1,
      );

      if (isCriticalAlertRecord(alert)) {
        criticalByBucket[bucketIndex] += 1;
      } else {
        warningByBucket[bucketIndex] += 1;
      }
    });

    const hourlyAlertPattern = HOUR_BUCKET_STARTS.map((hourStart, index) => ({
              time: `${String(hourStart).padStart(2, "0")}:00`,
      critical: criticalByBucket[index],
      warning: warningByBucket[index],
    }));

    return {
      deviceTypeCounts: Array.from(typeCounts, ([type, count]) => ({
        type,
        count,
        percent: Math.round((count / totalDevices) * 1000) / 10,
      })),
      statusBreakdown: [
        { name: t("Active"), value: statusCounts.Normal, color: ANALYTICS_STATUS_COLORS.ACTIVE },
        { name: t("Warning"), value: statusCounts.Warning, color: ANALYTICS_STATUS_COLORS.WARNING },
        { name: t("Critical"), value: statusCounts.Alert, color: ANALYTICS_STATUS_COLORS.CRITICAL },
        { name: t("Inactive"), value: statusCounts.Inactive, color: "#94A3B8" },
      ],
      summary: {
        totalDevices: devices.length,
        devicesOnline: devices.length - statusCounts.Inactive,
        devicesInAlert: criticalAlertCount,
        devicesInactive: statusCounts.Inactive,
        devicesWarning: warningAlertCount,
      },
      highestAlertAssets,
      alertsBySensor,
      hourlyAlertPattern,
    };
  }, [devices, analyticsAlerts, t]);

  const installationStopMarkers = useMemo(() => {
    return buildAlertLocationMarkers({ alerts: analyticsAlerts, devices, stops, t });
  }, [analyticsAlerts, devices, stops, t]);

  const analyticsWidgets = useMemo(
    () => {
      if (!Array.isArray(widgets) || widgets.length === 0) {
        return DEFAULT_ANALYTICS_WIDGETS;
      }

      if (
        widgets.length === 1 &&
        getAnalyticsWidgetKey(widgets[0]) === "deviceAnalytics"
      ) {
        return DEFAULT_ANALYTICS_WIDGETS;
      }

      return widgets;
    },
    [widgets],
  );

  const renderDeviceTypeDistribution = (widget) => (
    <AnalyticsTile
      key={widget.id || getAnalyticsWidgetKey(widget)}
      title={t(getAnalyticsWidgetTitle(widget, "Device Type Distribution"))}
      action={<AssetDistributionActions t={t} onRangeChange={setAnalyticsRange} />}
      sx={getAnalyticsWidgetGridSx(widget, { p: 1.25, gridColumn: "1 / -1" })}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
            lg: "repeat(7, minmax(0, 1fr))",
          },
          gap: 0.75,
        }}
      >
        {deviceTypeCounts.slice(0, 7).map((item) => (
          <Box
            key={item.type}
            sx={{
              border: "1px solid #D4D7DD",
              borderRadius: "6px",
              backgroundColor: "#F3F4F6",
              p: 0.85,
              minHeight: 74,
            }}
          >
            <TinyDeviceIcon type={item.type} color="#45474C" />
            <Typography sx={{ color: "#45474C", fontSize: 8.5, mt: 0.4 }}>
              {item.type}
            </Typography>
            <Typography
              sx={{
                color: "#1F242E",
                fontSize: 17,
                fontWeight: 800,
                lineHeight: "20px",
              }}
            >
              {item.count}
            </Typography>
            <Typography sx={{ color: "#647490", fontSize: 8 }}>
              {item.percent}%
            </Typography>
          </Box>
        ))}
      </Box>
    </AnalyticsTile>
  );

  const renderDeviceHealth = (widget) => (
    <AnalyticsTile
      key={widget.id || getAnalyticsWidgetKey(widget)}
      title={t(getAnalyticsWidgetTitle(widget, "Device Health"))}
      sx={getAnalyticsWidgetGridSx(widget, { minHeight: 300 })}
    >
      <ResponsiveContainer width="100%" height={185}>
        <PieChart>
          <Pie
            data={statusBreakdown}
            dataKey="value"
            nameKey="name"
            innerRadius={56}
            outerRadius={82}
            startAngle={90}
            endAngle={-270}
            isAnimationActive={false}
          >
            {statusBreakdown.map((entry) => (
              <Cell key={entry.name} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <text x="50%" y="47%" textAnchor="middle" fill="#647490" fontSize="9">
            {t("Total Devices")}
          </text>
          <text
            x="50%"
            y="60%"
            textAnchor="middle"
            fill="#1F242E"
            fontSize="26"
            fontWeight="800"
          >
            {summary.totalDevices}
          </text>
        </PieChart>
      </ResponsiveContainer>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1 }}>
        {statusBreakdown.map((entry) => (
          <Box key={entry.name} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "2px",
                backgroundColor: entry.color,
              }}
            />
            <Typography sx={{ fontSize: 9, color: "#45474C", flex: 1 }}>
              {entry.name}
            </Typography>
            <Typography sx={{ fontSize: 9, fontWeight: 700 }}>{entry.value}</Typography>
          </Box>
        ))}
      </Box>
    </AnalyticsTile>
  );

  const renderHighestAlertAssets = (widget) => (
    <AnalyticsTile
      key={widget.id || getAnalyticsWidgetKey(widget)}
      title={t(getAnalyticsWidgetTitle(widget, "Vehicles with Highest Alerts"))}
      sx={getAnalyticsWidgetGridSx(widget, { minHeight: 300 })}
    >
      <Box sx={{ display: "grid", gap: 1.1, mt: 1 }}>
        {(highestAlertAssets.length
          ? highestAlertAssets
          : [{ asset: t("No alerts"), count: 0 }]
        ).map((item, index) => {
          const max = Math.max(...highestAlertAssets.map((entry) => entry.count), 1);
          const color = [
            "#F53A2E",
            "#FF781C",
            "#F59E0B",
            "#F6C21A",
            "#F4D83D",
            "#23B85A",
            "#53CE7E",
            "#83DEA2",
          ][index % 8];
          return (
            <Box
              key={item.asset}
              sx={{
                display: "grid",
                gridTemplateColumns: "90px 1fr 24px",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  color: "#67809F",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {item.asset}
              </Typography>
              <Box
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: "#EEF2F7",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${(item.count / max) * 100}%`,
                    height: "100%",
                    borderRadius: 5,
                    backgroundColor: color,
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: 10, fontWeight: 700 }}>{item.count}</Typography>
            </Box>
          );
        })}
      </Box>
    </AnalyticsTile>
  );

  const renderAlertsBySensors = (widget) => (
    <AnalyticsTile
      key={widget.id || getAnalyticsWidgetKey(widget)}
      title={t(getAnalyticsWidgetTitle(widget, "Alerts by Sensors"))}
      action={<Typography sx={{ color: "#45474C", fontSize: 20, lineHeight: 1 }}>⋮</Typography>}
      sx={getAnalyticsWidgetGridSx(widget, { minHeight: 300, gridColumn: { lg: "span 2" } })}
    >
      <ResponsiveContainer width="100%" height={235}>
        <BarChart
          data={
            alertsBySensor.length
              ? alertsBySensor
              : [{ type: t("No alerts"), count: 0, color: "#CBD5E1" }]
          }
          margin={{ top: 10, right: 8, left: 0, bottom: 25 }}
        >
          <XAxis dataKey="type" tick={{ fontSize: 9, fill: "#1F242E" }} interval={0} />
          <YAxis hide />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="count" barSize={46} shape={<RoundedBar />} isAnimationActive={false}>
            {(alertsBySensor.length
              ? alertsBySensor
              : [{ type: t("No alerts"), count: 0, color: "#CBD5E1" }]
            ).map((entry) => (
              <Cell key={entry.type} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </AnalyticsTile>
  );

  const renderInstallationLocationAlerts = (widget) => (
    <AnalyticsTile
      key={widget.id || getAnalyticsWidgetKey(widget)}
      title={t(getAnalyticsWidgetTitle(widget, "Alerts by Installation Location (Sites)"))}
      sx={getAnalyticsWidgetGridSx(widget, { minHeight: 315, gridColumn: { lg: "span 2" } })}
    >
      <InstallationAlertsMap stops={installationStopMarkers} />
    </AnalyticsTile>
  );

  const renderHourlyAlertPattern = (widget) => (
    <AnalyticsTile
      key={widget.id || getAnalyticsWidgetKey(widget)}
      title={t(getAnalyticsWidgetTitle(widget, "24 Hour Alert Pattern"))}
      sx={getAnalyticsWidgetGridSx(widget, { minHeight: 315, gridColumn: { lg: "span 2" } })}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Typography sx={{ fontSize: 10, color: "#647490" }}>
            <Box
              component="span"
              sx={{
                display: "inline-block",
                width: 10,
                height: 10,
                mr: 0.5,
                backgroundColor: ANALYTICS_STATUS_COLORS.CRITICAL,
              }}
            />
            {t("Critical Alerts")}
          </Typography>
          <Typography sx={{ fontSize: 10, color: "#647490" }}>
            <Box
              component="span"
              sx={{
                display: "inline-block",
                width: 10,
                height: 10,
                mr: 0.5,
                backgroundColor: ANALYTICS_STATUS_COLORS.WARNING,
              }}
            />
            {t("Warning Alerts")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 5 }}>
          <SummaryStat
            label={t("Total Critical Alerts")}
            value={summary.devicesInAlert}
            color={ANALYTICS_STATUS_COLORS.CRITICAL}
          />
          <SummaryStat
            label={t("Total Warning Alerts")}
            value={summary.devicesWarning}
            color={ANALYTICS_STATUS_COLORS.WARNING}
          />
        </Box>
      </Box>
      <ResponsiveContainer width="100%" height={205}>
        <AreaChart data={hourlyAlertPattern} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#EDF1F5" vertical={false} />
          <XAxis dataKey="time" tick={{ fontSize: 10, fill: "#8B9BB0" }} />
          <YAxis hide />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="warning"
            stackId="1"
            stroke={ANALYTICS_STATUS_COLORS.WARNING}
            fill={ANALYTICS_STATUS_COLORS.WARNING}
            isAnimationActive={false}
          />
          <Area
            type="monotone"
            dataKey="critical"
            stackId="1"
            stroke={ANALYTICS_STATUS_COLORS.CRITICAL}
            fill={ANALYTICS_STATUS_COLORS.CRITICAL}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </AnalyticsTile>
  );

  const renderAnalyticsWidget = (widget) => {
    const key = getAnalyticsWidgetKey(widget);

    switch (key) {
      case "deviceTypeDistribution":
      case "devicesByType":
      case "deviceTypes":
        return renderDeviceTypeDistribution(widget);
      case "deviceHealth":
      case "health":
      case "deviceStatus":
        return renderDeviceHealth(widget);
      case "highestAlertAssets":
      case "highestAlerts":
      case "vehiclesWithHighestAlerts":
        return renderHighestAlertAssets(widget);
      case "alertsBySensors":
      case "alertsBySensor":
      case "sensorAlerts":
        return renderAlertsBySensors(widget);
      case "installationLocationAlerts":
      case "alertsByInstallationLocation":
      case "alertInstallationSites":
        return renderInstallationLocationAlerts(widget);
      case "hourlyAlertPattern":
      case "alertPattern":
      case "twentyFourHourAlertPattern":
        return renderHourlyAlertPattern(widget);
      default:
        return null;
    }
  };

  return (
    <Box
      elevation={0}
      sx={{
        // border: "1px solid #E7ECF3",
        borderRadius: "8px",
        overflow: "hidden",
        // backgroundColor: "#FFFFFF",
        mt: 2,
      }}
    >
      <Box
        sx={{
          px: 1.5,
          pt: 1,
          pb: 0.75,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography
       sx={{
    color: "#45474C",
    fontFamily: "Inter",
    fontWeight: 700,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: "1.2px",
    textAlign: "center",
    verticalAlign: "middle",
    textTransform: "uppercase",
  }}
        >
          {t("ANALYTICS")}
        </Typography>
        {/* <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          <SummaryStat label={t("Total Devices")} value={summary.totalDevices} />
          <SummaryStat
            label={t("Online")}
            value={summary.devicesOnline}
            color="#177245"
          />
          <SummaryStat
            label={t("In Alert")}
            value={summary.devicesInAlert}
            color="#AF0000"
          />
          <SummaryStat
            label={t("Inactive")}
            value={summary.devicesInactive}
            color="#6B7280"
          />
        </Box> */}
      </Box>

      <Box
        sx={{
          px: 1.5,
          pb: 1.5,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        {analyticsWidgets.map(renderAnalyticsWidget).filter(Boolean)}
        {false && (
          <>
        <AnalyticsTile
          title={t("Alerts by Sensors")}
          action={
            <Typography sx={{ color: "#45474C", fontSize: 20, lineHeight: 1 }}>
              ⋮
            </Typography>
          }
          sx={{ minHeight: 300, gridColumn: { lg: "span 2" } }}
        >
          <ResponsiveContainer width="100%" height={235}>
            <BarChart
              data={
                alertsBySensor.length
                  ? alertsBySensor
                  : [{ type: t("No alerts"), count: 0, color: "#CBD5E1" }]
              }
              margin={{ top: 10, right: 8, left: 0, bottom: 25 }}
            >
              <XAxis
                dataKey="type"
                tick={{ fontSize: 9, fill: "#1F242E" }}
                interval={0}
              />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="count"
                barSize={46}
                shape={<RoundedBar />}
                isAnimationActive={false}
              >
                {(alertsBySensor.length
                  ? alertsBySensor
                  : [{ type: t("No alerts"), count: 0, color: "#CBD5E1" }]
                ).map((entry) => (
                  <Cell key={entry.type} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </AnalyticsTile>

        <AnalyticsTile
          title={t("Alerts by Installation Location (Sites)")}
          sx={{ minHeight: 315, gridColumn: { lg: "span 2" } }}
        >
          <InstallationAlertsMap stops={installationStopMarkers} />
        </AnalyticsTile>

        <AnalyticsTile
          title={t("24 Hour Alert Pattern")}
          sx={{ minHeight: 315, gridColumn: { lg: "span 2" } }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Typography sx={{ fontSize: 10, color: "#647490" }}>
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    mr: 0.5,
                    backgroundColor: ANALYTICS_STATUS_COLORS.CRITICAL,
                  }}
                />
                {t("Critical Alerts")}
              </Typography>
              <Typography sx={{ fontSize: 10, color: "#647490" }}>
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    mr: 0.5,
                    backgroundColor: ANALYTICS_STATUS_COLORS.WARNING,
                  }}
                />
                {t("Warning Alerts")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 5 }}>
              <SummaryStat
                label={t("Total Critical Alerts")}
                value={summary.devicesInAlert}
                color={ANALYTICS_STATUS_COLORS.CRITICAL}
              />
              <SummaryStat
                label={t("Total Warning Alerts")}
                value={summary.devicesWarning}
                color={ANALYTICS_STATUS_COLORS.WARNING}
              />
            </Box>
          </Box>
          <ResponsiveContainer width="100%" height={205}>
            <AreaChart
              data={hourlyAlertPattern}
              margin={{ top: 20, right: 10, left: 0, bottom: 0 }}
            >
              <CartesianGrid stroke="#EDF1F5" vertical={false} />
              <XAxis dataKey="time" tick={{ fontSize: 10, fill: "#8B9BB0" }} />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="warning"
                stackId="1"
                stroke={ANALYTICS_STATUS_COLORS.WARNING}
                fill={ANALYTICS_STATUS_COLORS.WARNING}
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="critical"
                stackId="1"
                stroke={ANALYTICS_STATUS_COLORS.CRITICAL}
                fill={ANALYTICS_STATUS_COLORS.CRITICAL}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </AnalyticsTile>
          </>
        )}
      </Box>
    </Box>
  );
}
